import { ProductInfoType } from 'src/types/product';
import type { ProductData } from 'src/types/services';
import { getSdk } from 'src/sdk';
import type { ProductResponseType as SdkProductResponse, ProductVersionsResponseType } from '@rendering/web-client';

const ProductType: Record<string, string> = {
    CIM: 'CIM',
    PRD: 'PRD',
    VIP: 'VIP',
};

/**
 * The SDK resolves with the bare entity (no axios-style `{ data }` envelope), and its shape depends on
 * the product type: a VIP (MCP sku) record, a CIM product entity or a PRD version. Each keeps its
 * "last modified" date somewhere different; a PRD version only has its created date, as it might be the
 * first iteration of the product.
 */
function mapSdkProductResponse(result: SdkProductResponse): ProductData {
    const { name, description } = result;

    if ('mcpSku' in result) {
        return { name, description, modified: result.modified };
    }

    if ('auditInfo' in result) {
        return { name, description, modified: result.auditInfo?.modifiedAt, options: result.options };
    }

    return { name, description, modified: result.createdAt };
}

export function retrieveProductData({ sku, skuVersion }: Omit<ProductInfoType, 'attributes'>) {
    const request = skuVersion ? getSdk().Products.getByVersion(sku, skuVersion) : getSdk().Products.getMCPFromVIP(sku);
    return request.then(mapSdkProductResponse);
}

function mapVersionsResponse(res: ProductVersionsResponseType, sku: string) {
    const items = res._embedded?.item;
    // The SDK types `item` as optional; a response without it is malformed, so reject
    // and let the callers fall back to the V1 product lookup instead of treating it as "no versions".
    if (!items) {
        throw new Error(`No versions found in the response for ${sku}`);
    }

    return items
        .sort((a, b) => (b.version || 0) - (a.version || 0))
        .map((proVer) => ({
            productId: proVer.productId,
            version: proVer.version,
            status: proVer.status.toLowerCase(),
            current: 'current' in proVer ? proVer.current : undefined,
        }));
}

/**
 * @description This method is using multiple request to build our data because limitations from product orchestration API.
 * We are expecting this to change in the future, but for now this is the only way to do it.
 */
export function getPRDProductVersions(sku: string) {
    return getSdk()
        .Products.getPRDProductVersions(sku, true)
        .then((res) => mapVersionsResponse(res, sku));
}

export function getProductVersions(sku: string) {
    const type = ProductType[sku.trim().substring(0, 3)];

    if (type === 'VIP') {
        return Promise.resolve([]);
    }

    if (type === ProductType.PRD) {
        return getPRDProductVersions(sku);
    }

    return getSdk()
        .Products.getVersions(sku)
        .then((res) => mapVersionsResponse(res, sku));
}

/**
 * Retrieves mcpSku and/or version of the mcpSku related to the vistaprint platform sku + version.
 * @param options
 * @param options.sku - The platformID.
 * @param options.version - The platformID version.
 * @returns - The MCP Sku and version related to the platformId .
 */
export function retrievePlatformId({ sku, skuVersion, attributes }: ProductInfoType) {
    // This whole flow could be called via `getMCPFromPRD` but scene-maker only uses
    // `getMCPFromPRDWithAttributes` with and without attributes when version is provided
    if (skuVersion) {
        return getSdk()
            .Products.getMCPFromPRDWithAttributes(sku, skuVersion.toString(), attributes ?? {})
            .then((res) => {
                return {
                    merchantSku: res.productId,
                    merchantVersion: res.version,
                    merchantVariables: res.attributes,
                };
            });
    }

    return getSdk()
        .Products.getMCPFromPRD(sku)
        .then((res) => {
            return {
                merchantSku: res.productId,
                // The SDK no version branch goes to `getMCPFromPRDWithoutVersion` where it
                // hard-codes `version: 0` for the version-less lookup; the catalog sends none, so it is unknown.
                merchantVersion: undefined,
                merchantVariables: res.attributes ?? undefined,
            };
        });
}
